import { useQuery } from "@tanstack/react-query";
import { Cpu } from "lucide-react";

import { api, type WorkerGpu } from "../../api";
import { EmptyState, Note, SettingsSection } from "./shared";

function Gauge({ label, pct, detail }: { label: string; pct: number | null; detail?: string }) {
  const value = pct == null ? null : Math.max(0, Math.min(100, pct));
  const tone =
    value == null ? "bg-white/10" : value >= 90 ? "bg-red-400" : value >= 70 ? "bg-amber-300" : "bg-emerald-400";
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-bf-muted">{label}</span>
        <span className="tabular-nums text-bf-text">{detail ?? (value == null ? "—" : `${Math.round(value)}%`)}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className={`h-full ${tone}`} style={{ width: `${value ?? 0}%` }} />
      </div>
    </div>
  );
}

const gib = (mib: number | null) => (mib == null ? "—" : (mib / 1024).toFixed(1));

function ago(s: number) {
  if (s < 90) return `${Math.round(s)} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

function OfflineRow({ w }: { w: WorkerGpu }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/5 px-4 py-2.5 text-sm">
      <div className="min-w-0">
        <span className="text-bf-text">{w.id}</span>
        <span className="ml-2 text-xs text-bf-muted">{w.gpu}</span>
      </div>
      <span className="text-xs text-bf-muted">Not running · last seen {ago(w.age_s)}</span>
    </div>
  );
}

function WorkerCard({ w }: { w: WorkerGpu }) {
  const s = w.stats;
  const memPct =
    s?.mem_used_mib != null && s.mem_total_mib ? (100 * s.mem_used_mib) / s.mem_total_mib : null;
  const stale = w.stats_age_s != null && w.stats_age_s > 60;
  return (
    <div className="bf-card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-white">{w.id}</p>
          <p className="truncate text-xs text-bf-muted">{w.gpu || "GPU model not reported"}</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-bf-muted">
            {w.cameras} camera{w.cameras === 1 ? "" : "s"}
          </span>
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-300">Online</span>
        </div>
      </div>
      {s ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Gauge label="GPU" pct={s.util_pct} />
            <Gauge
              label="VRAM"
              pct={memPct}
              detail={`${gib(s.mem_used_mib)} / ${gib(s.mem_total_mib)} GiB`}
            />
            <Gauge label="Decode (NVDEC)" pct={s.dec_pct} />
            <Gauge label="Encode (NVENC)" pct={s.enc_pct} />
          </div>
          <p className="text-xs text-bf-muted">
            {s.temp_c != null && `${Math.round(s.temp_c)} °C · `}
            {s.power_w != null && `${Math.round(s.power_w)} W · `}
            {stale ? "last sample " : "sampled "}
            {Math.round(w.stats_age_s ?? 0)} s ago
          </p>
        </>
      ) : (
        <p className="text-sm text-bf-muted">No GPU sample from this worker yet.</p>
      )}
    </div>
  );
}

/** Live GPU load of every ingest worker, rented or on-site. */
export function GpuWorkersSection() {
  const gpus = useQuery({
    queryKey: ["worker-gpus"],
    queryFn: api.workers.gpus,
    refetchInterval: 5_000,
  });
  const rows = gpus.data ?? [];
  const live = rows.filter((w) => w.live);
  const stopped = rows.filter((w) => !w.live).sort((a, b) => a.age_s - b.age_s);
  return (
    <SettingsSection
      title="GPU workers"
      description="Load on each worker's GPU, refreshed with its heartbeat."
      hint={
        <>
          Figures are for the whole card, every process on it: a worker that shares its GPU
          with a model server shows both. Each worker samples about every 15 seconds.
        </>
      }
    >
      {gpus.isError && <Note tone="error">Could not load the workers.</Note>}
      {gpus.isSuccess && rows.length === 0 ? (
        <EmptyState icon={Cpu} title="No workers registered" />
      ) : (
        <div className="space-y-3">
          {live.map((w) => (
            <WorkerCard key={w.id} w={w} />
          ))}
          {stopped.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {stopped.map((w) => (
                <OfflineRow key={w.id} w={w} />
              ))}
            </div>
          )}
        </div>
      )}
    </SettingsSection>
  );
}
